import _capitalize from 'lodash/capitalize';

class TextUtils {
  /**
   * Converts the string to title case by splitting on spaces and capitalizing each word.
   *
   * Any non-string will return '';
   *
   * @example TextUtils.titleCase('what does the cat say') => 'What Does The Cat Say'
   * @example TextUtils.titleCase('MEOW MEOW MEOW') => 'Meow Meow Meow';
   */
  static titleCase(text?: string | null): string {
    if (typeof text !== 'string') return '';
    return text
      .split(' ')
      .map((word) => {
        if (word[0] === `(`) {
          // if a name start with ( lodash will not capitalize the second letter. This removes the parenthesis, capitalizes the letter and adds the parenthesis back in.
          return `(${_capitalize(word.slice(1))}`;
        }
        return _capitalize(word);
      })
      .join(' ');
  }

  /**
   * Converts the string to sentence case by making everything lowercase,
   * matching any character that begins a sentence, and making it uppercase.
   *
   * Any non-string will return '';
   *
   * @example TextUtils.sentenceCase('Couples luncheon, Rehearsal dinner, Shower, Welcome party') => 'Couples luncheon, rehearsal dinner, shower, welcome party'
   * @example TextUtils.sentenceCase('Baraat, Ceremony, Cocktail hour, Reception') => 'Baraat, ceremony, cocktail hour, reception';
   */
  static sentenceCase(text?: string | null): string {
    if (typeof text !== 'string') return '';
    return text.toLowerCase().replace(/(^\s*\w|[.!?]\s*\w)/g, (char) => {
      return char.toUpperCase();
    });
  }

  /**
   * Formats a number as a number with commas in the places expected in the united states
   *
   * Any non-number will return '';
   *
   * @example TextUtils.formatWithCommas(1000) => '1,000'
   * @example TextUtils.formatWithCommas(500) => '500'
   * @example TextUtils.formatWithCommas(1000*1000) => '1,000,000'
   *
   */
  static formatWithCommas(number?: number | null): string {
    if (typeof number !== 'number') return '';
    return new Intl.NumberFormat('en-US').format(number);
  }

  /**
   * Formats a number as a dollar (US) price, including commas where appropriate
   *
   * Any non-number will return '', except for Strings that can be parsed with Number(<value>).
   *
   * @example TextUtils.formatAsCurrency(1000) => '$1,000'
   * @example TextUtils.formatAsCurrency(1000, 2) => '$1,000.00'
   * @example TextUtils.formatAsCurrency(42.42) => '$42.42'
   */
  static formatAsCurrency(
    number?: number | string | null,
    minimumFractionDigits: number | undefined = undefined
  ): string {
    let isValid = false;
    if (typeof number === 'number' && !Number.isNaN(number)) {
      isValid = true;
    }
    if (typeof number === 'string' && !Number.isNaN(Number(number))) {
      isValid = true;
    }
    if (!isValid) return '';

    const asNumber = Number(number);
    const hasCents = asNumber - Math.floor(asNumber) > 0;

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: minimumFractionDigits || (hasCents ? 2 : 0),
    }).format(Number(number));
  }

  /**
   * Formats a number range with a dash separating the low and
   * high values in the range.  If either of the values in the
   * range are not numbers, only the other value will be returned.
   * If both values are not-numbers, this will return empty string
   *
   * @example TextUtils.formatRange(50, 1000) => '50-1000'
   * @example TextUtils.formatRange(undefined, 1000) => '1000'
   * @example TextUtils.formatRange(50, undefined) => '50'
   *
   * @see rangeUtils For a more powerful range formatter,
   */
  static formatRange(min: number | null, max: number | null): string {
    if (typeof min === 'number' && typeof max === 'number') {
      if (min === max) {
        return min.toString();
      }
      return `${min}-${max}`;
    }

    if (typeof min === 'number') {
      return min.toString();
    }
    if (typeof max === 'number') {
      return max.toString();
    }
    return '';
  }

  static formatArrayWithOxfordComma = (values: string[], connector: 'and' | 'or') => {
    if (values.length <= 2) {
      return values.join(` ${connector} `);
    }
    const lastValue = values.pop();
    return `${values.join(', ')}, ${connector} ${lastValue}`;
  };
}

export default TextUtils;

/**
 * returns people/person based on the num input
 *
 * @example peopleTerm(1) => '1 person'
 * @example peopleTerm(10) => '10 people'
 * @example peopleTerm(-10) => null
 * @example peopleTerm(null) => null
 */
export const peopleTerm = (num: number | null): string | null => {
  if (num === 1) {
    return '1 person';
  }

  if ((num && num > 1) || num === 0) {
    return `${num} people`;
  }

  return null;
};

/**
 * returns the hashtag version of the string
 *
 * @example formatHashtag('wedding') => '#wedding'
 * @example formatHashtag('RINGS') => '#rings'
 * @example formatHashtag('Place Setting') => '#placesetting'
 */
export const formatHashtag = (text: string): string =>
  `#${text.toLowerCase().replace(/[^a-z0-9]/i, '')}`;
